import React, { useState, useContext } from "react";
import {
  Box,
  makeStyles,
  IconButton,
  Tooltip,
  Popover,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@material-ui/core";
import { InfoOutlined as InfoIcon } from "@material-ui/icons";
import Card from "../../../../../components/Card/Card.js";
import CardBody from "../../../../../components/Card/CardBody.js";
import InteractiveMap from "./InteractiveMap.js";
import TopClients from "./TopClients.js";
import PropTypes from "prop-types";
import AuthContext from "../../../../../connections/auth/AuthProvider";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";

const cardVisualMapStyle = (theme, clientColor) => ({
  ...customSelectStyle(theme, clientColor),
  card: {
    flex: 1,
    minWidth: "300px",
    height: "100%",
    maxHeight: "100%",
    display: "flex",
    flexDirection: "column",
    marginTop: theme.spacing(0),
    overflow: "hidden",
  },
  content: {
    padding: theme.spacing(1),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minHeight: "auto",
    gap: theme.spacing(1),
    position: "relative",
    zIndex: 2,
  },
  firstRowControls: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 1001,
  },
  centerControls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
  },
  colorControlsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    marginLeft: "2px",
    marginRight: "2px",
    gap: theme.spacing(1),
  },
  selectorControl: {
    minWidth: "140px",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: grayColor[4] + "!important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
    "& .MuiInputLabel-root": {
      "&.Mui-focused": {
        color: clientColor + "!important",
      },
    },
  },

  mapDataTypeButtons: {
    "& .MuiButton-root": {
      padding: "4px 12px",
      fontSize: "0.7rem",
      fontWeight: 500,
      textTransform: "none",
      border: "1px solid rgba(0, 0, 0, 0.23)",
      color: "rgba(0, 0, 0, 0.87)",
      backgroundColor: "white",
      "&:hover": {
        color: "white",
        backgroundColor: theme.palette.grey[500],
      },
    },
    "& .MuiButtonGroup-root": {
      borderRadius: 6,
    },
  },
  mapDataTypeButtonSelected: {
    backgroundColor: `${theme.palette.grey[400]} !important`,
    color: "white !important",
  },
  viewSwitcher: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  iconButton: {
    padding: theme.spacing(0.75),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
  },
  selectedIcon: {
    padding: theme.spacing(0.75),
    border: `1px solid ${clientColor}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: `${clientColor}15`,
    color: clientColor,
    "&:hover": {
      backgroundColor: `${clientColor}25`,
      color: clientColor,
    },
  },
  contentContainer: {
    flex: 1,
    padding: theme.spacing(1),
    minHeight: "300px",
    overflow: "hidden",
    position: "relative",
  },
  infoButtonInContent: {
    position: "absolute",
    bottom: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 1000,
  },
  infoPopover: {
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0px 5px 15px 0px rgba(0, 0, 0, 0.3)",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    maxWidth: 280,
    fontSize: "0.75rem",
  },
  infoTitle: {
    fontWeight: "bold",
    fontSize: "0.875rem",
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  infoSection: {
    marginBottom: theme.spacing(1.5),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  infoLabel: {
    fontWeight: "600",
    fontSize: "0.75rem",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  infoItem: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.25),
    lineHeight: 1.3,
  },
});

export default function CardVisualMap({
  selectedScenario,
  appliedFilters,
  currentView,
  selectedVolume = "input_sellthrough_volume_sales",
}) {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;
  const useStyles = makeStyles((theme) =>
    cardVisualMapStyle(theme, clientColor),
  );
  const classes = useStyles();

  const [mapDataType, setMapDataType] = useState("sellthrough");
  const [selectedColorMetric, setSelectedColorMetric] = useState("margin");
  const [selectedTooltipDetail, setSelectedTooltipDetail] = useState("channel");
  const [selectedLocation, setSelectedLocation] = useState("location_option_2");
  const [infoPopoverAnchor, setInfoPopoverAnchor] = useState(null);

  // Location options mapping - frontend agnostic keys
  const LOCATION_OPTIONS = [
    { value: "location_option_1", label: "Origem Sellin" },
    { value: "location_option_2", label: "Destino Sellin" },
    { value: "location_option_3", label: "Destino Sellthrough/Sellout" },
  ];

  const handleDataTypeChange = (dataType) => {
    setMapDataType(dataType);
  };

  const handleColorMetricChange = (metric) => {
    setSelectedColorMetric(metric);
  };

  const handleTooltipDetailChange = (detail) => {
    setSelectedTooltipDetail(detail);
  };

  const handleLocationChange = (location) => {
    setSelectedLocation(location);
  };

  const handleInfoClick = (event) => {
    setInfoPopoverAnchor(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoPopoverAnchor(null);
  };

  return (
    <Card className={classes.card}>
      <CardBody className={classes.content}>
        {/* Header with two rows of controls */}
        <Box className={classes.header}>
          {/* First row: Price/Margin selector, Location selector, and Highlights selector (only show when map view is active AND scenario selected) */}
          {currentView === "map" && selectedScenario && (
            <Box className={classes.colorControlsRow}>
              <FormControl className={classes.selectorControl} size="small">
                <InputLabel id="price-margin-selector-label">
                  Price and Margin
                </InputLabel>
                <Select
                  labelId="price-margin-selector-label"
                  value={mapDataType}
                  onChange={(e) => handleDataTypeChange(e.target.value)}
                  label="Price and Margin"
                  MenuProps={{
                    className: classes.selectMenu,
                    classes: { paper: classes.selectPaper },
                  }}
                >
                  <MenuItem value="sellin" className={classes.selectMenuItem}>
                    Sell In
                  </MenuItem>
                  <MenuItem
                    value="sellthrough"
                    className={classes.selectMenuItem}
                  >
                    Sell Through
                  </MenuItem>
                  <MenuItem value="sellout" className={classes.selectMenuItem}>
                    Sell Out
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl className={classes.selectorControl} size="small">
                <InputLabel id="location-selector-label">
                  By Location
                </InputLabel>
                <Select
                  labelId="location-selector-label"
                  value={selectedLocation}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  label="By Location"
                  MenuProps={{
                    className: classes.selectMenu,
                    classes: { paper: classes.selectPaper },
                  }}
                >
                  {LOCATION_OPTIONS.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      className={classes.selectMenuItem}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl className={classes.selectorControl} size="small">
                <InputLabel id="tooltip-detail-selector-label">
                  Highlights
                </InputLabel>
                <Select
                  labelId="tooltip-detail-selector-label"
                  value={selectedTooltipDetail}
                  onChange={(e) => handleTooltipDetailChange(e.target.value)}
                  label="Highlights"
                  MenuProps={{
                    className: classes.selectMenu,
                    classes: { paper: classes.selectPaper },
                  }}
                >
                  <MenuItem value="channel" className={classes.selectMenuItem}>
                    Channel
                  </MenuItem>
                  <MenuItem value="industry" className={classes.selectMenuItem}>
                    Industry
                  </MenuItem>
                  <MenuItem value="client" className={classes.selectMenuItem}>
                    Client
                  </MenuItem>
                  <MenuItem
                    value="pointofsale"
                    className={classes.selectMenuItem}
                  >
                    Point of Sale
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

        {/* Content Container */}
        <Box className={classes.contentContainer}>
          {currentView === "map" ? (
            <InteractiveMap
              selectedScenario={selectedScenario}
              appliedFilters={appliedFilters}
              height="100%"
              dataType={mapDataType}
              selectedColorMetric={selectedColorMetric}
              selectedTooltipDetail={selectedTooltipDetail}
              selectedLocation={selectedLocation}
              selectedVolume={selectedVolume}
              onDataTypeChange={handleDataTypeChange}
              onColorMetricChange={handleColorMetricChange}
              showControls={false}
            />
          ) : (
            <TopClients
              selectedScenario={selectedScenario}
              appliedFilters={appliedFilters}
              height="100%"
            />
          )}

          {/* Info button - positioned at bottom right, only show when scenario selected */}
          {selectedScenario && (
            <Tooltip title="Map Tooltip Rules">
              <IconButton
                onClick={handleInfoClick}
                className={`${classes.iconButton} ${classes.infoButtonInContent}`}
                size="small"
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Info Popover */}
        <Popover
          open={Boolean(infoPopoverAnchor)}
          anchorEl={infoPopoverAnchor}
          onClose={handleInfoClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            className: classes.infoPopover,
          }}
          transitionDuration={300}
        >
          <Box>
            <Typography className={classes.infoTitle}>
              Geographic Map Guide
            </Typography>

            <Box className={classes.infoSection}>
              <Typography className={classes.infoLabel}>Data Types:</Typography>
              <Typography className={classes.infoItem}>
                <strong>Sell In:</strong> Manufacturer → Distributor flow
              </Typography>
              <Typography className={classes.infoItem}>
                <strong>Sell Through:</strong> Distributor → Retailer flow
              </Typography>
              <Typography className={classes.infoItem}>
                <strong>Sell Out:</strong> Retailer → Consumer flow
              </Typography>
            </Box>

            <Box className={classes.infoSection}>
              <Typography className={classes.infoLabel}>
                Color Metrics:
              </Typography>
              <Typography className={classes.infoItem}>
                <strong>Price Net:</strong> Price after discounts, before taxes
              </Typography>
              <Typography className={classes.infoItem}>
                <strong>Margin:</strong> Profit percentage (Sell Through/Out
                only)
              </Typography>
              <Typography className={classes.infoItem}>
                <strong>Taxes:</strong> Tax percentage added to net price
              </Typography>
              <Typography className={classes.infoItem}>
                <strong>Price Gross:</strong> Final price including taxes
              </Typography>
            </Box>

            <Box className={classes.infoSection}>
              <Typography className={classes.infoLabel}>
                Color Scale Algorithm:
              </Typography>
              <Typography className={classes.infoItem}>
                Dynamic quartile-based distribution with outlier removal
              </Typography>
              <Typography className={classes.infoItem}>
                🔴 Red: Bottom 25% (Q1)
                <br />
                🟡 Yellow: Q1-Q2
                <br />
                🟢 Green: Q3-Q4
              </Typography>
              <Typography className={classes.infoItem}>
                Region color = Average of all data in that region
              </Typography>
            </Box>

            <Box className={classes.infoSection}>
              <Typography className={classes.infoLabel}>
                Calculations:
              </Typography>
              <Typography className={classes.infoItem}>
                Volume-weighted averages using{" "}
                <strong>input_sellthrough_volume_sales</strong>
              </Typography>
              <Typography className={classes.infoItem}>
                Margin % = (margin_value / price_net) × 100
              </Typography>
              <Typography className={classes.infoItem}>
                Taxes % = ((price_gross - price_net) / price_net) × 100
              </Typography>
              <Typography className={classes.infoItem}>
                Prices aggregate across all filtered products
              </Typography>
            </Box>

            <Box className={classes.infoSection}>
              <Typography className={classes.infoLabel}>
                Tooltip Details:
              </Typography>
              <Typography className={classes.infoItem}>
                <strong>Channel:</strong> Breakdown by sales channel (default)
              </Typography>
              <Typography className={classes.infoItem}>
                <strong>Industry:</strong> Breakdown by production site/industry
              </Typography>
              <Typography className={classes.infoItem}>
                <strong>Client:</strong> Breakdown by client/customer
              </Typography>
              <Typography className={classes.infoItem}>
                <strong>Point of Sale:</strong> Breakdown by retail point of
                sale
              </Typography>
              <Typography className={classes.infoItem}>
                Data sourced from scenario&apos;s output table
              </Typography>
              <Typography className={classes.infoItem}>
                Brazil regions mapped by state code (2 letters)
              </Typography>
            </Box>
          </Box>
        </Popover>
      </CardBody>
    </Card>
  );
}

CardVisualMap.propTypes = {
  selectedScenario: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  appliedFilters: PropTypes.shape({
    product: PropTypes.string,
    brand: PropTypes.string,
  }),
  currentView: PropTypes.string.isRequired,
  selectedVolume: PropTypes.string,
};
